import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { compressImages } from '../lib/image';
import { useUpload } from '../lib/useUpload';
import type { Property, State, Area, Feature } from '../types';
import { Modal, Alert, UploadProgress, inputClass, labelClass, primaryBtn, secondaryBtn } from './ui';

interface FormState {
  title: string;
  description: string;
  location: string;
  state: string;
  area: string;
  property_type: string;
  listing_type: string;
  bedrooms: number;
  bathrooms: number;
  price: number;
  currency: string;
  leasing_type: string;
  minimum_stay: number;
  management_description: string;
  is_featured: boolean;
  featured_section: string;
  featured_order: number;
  is_active: boolean;
  is_sold: boolean;
  sold_date: string;
  features: string[];
}

const emptyForm: FormState = {
  title: '', description: '', location: '', state: '', area: '',
  property_type: 'duplex', listing_type: 'sale', bedrooms: 0, bathrooms: 0,
  price: 0, currency: 'NGN', leasing_type: '', minimum_stay: 0,
  management_description: '', is_featured: false, featured_section: '',
  featured_order: 0, is_active: true, is_sold: false, sold_date: '', features: [],
};

interface Props {
  open: boolean;
  onClose: () => void;
  editing: Property | null;
  onSaved: () => void;
}

export default function PropertyFormModal({ open, onClose, editing, onSaved }: Props) {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [videoFiles, setVideoFiles] = useState<File[]>([]);
  const upload = useUpload();
  const [error, setError] = useState('');
  const [states, setStates] = useState<State[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [features, setFeatures] = useState<Feature[]>([]);

  useEffect(() => { api.get<State[]>('/states').then(setStates).catch(() => {}); }, []);
  useEffect(() => { api.get<Area[]>('/areas').then(setAreas).catch(() => {}); }, []);
  useEffect(() => { api.get<Feature[]>('/features').then(setFeatures).catch(() => {}); }, []);

  useEffect(() => {
    if (open) {
      setError('');
      setImageFiles([]);
      setVideoFiles([]);
      if (editing) {
        setForm({
          title: editing.title,
          description: editing.description || '',
          location: editing.location || '',
          state: editing.state || '',
          area: editing.area || '',
          property_type: editing.property_type,
          listing_type: editing.listing_type,
          bedrooms: editing.bedrooms,
          bathrooms: editing.bathrooms,
          price: editing.price,
          currency: editing.currency,
          leasing_type: editing.leasing_type || '',
          minimum_stay: editing.minimum_stay || 0,
          management_description: editing.management_description || '',
          is_featured: editing.is_featured,
          featured_section: editing.featured_section || '',
          featured_order: editing.featured_order || 0,
          is_active: editing.is_active,
          is_sold: editing.is_sold,
          sold_date: editing.sold_date || '',
          features: editing.features,
        });
      } else {
        setForm(emptyForm);
      }
    }
  }, [open, editing]);

  const areaOptions = useMemo(
    () => {
      const stateId = states.find((s) => s.name === form.state)?.id;
      return areas.filter((a) => a.state_id === stateId);
    },
    [states, areas, form.state]
  );

  const toggleFeature = (name: string) => {
    setForm((f) => ({
      ...f,
      features: f.features.includes(name) ? f.features.filter((x) => x !== name) : [...f.features, name],
    }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    upload.reset();
    setError('');
    try {
      const fd = new FormData();
      const fields: Record<string, string | number | boolean> = {
        title: form.title,
        description: form.description,
        location: form.location,
        state: form.state,
        area: form.area,
        property_type: form.property_type,
        listing_type: form.listing_type,
        bedrooms: form.bedrooms,
        bathrooms: form.bathrooms,
        price: form.price,
        currency: form.currency,
        leasing_type: form.leasing_type,
        minimum_stay: form.minimum_stay,
        management_description: form.management_description,
        is_featured: form.is_featured,
        featured_section: form.featured_section,
        featured_order: form.featured_order,
        is_active: form.is_active,
        is_sold: form.is_sold,
        sold_date: form.sold_date,
        features: JSON.stringify(form.features),
      };
      Object.entries(fields).forEach(([k, v]) => fd.append(k, String(v)));
      const compressed = await compressImages(imageFiles, 1600, 0.82);
      compressed.forEach((file) => fd.append('images', file));
      videoFiles.forEach((file) => fd.append('videos', file));

      if (editing) {
        await upload.run(`/properties/${editing.id}`, fd, 'PUT');
      } else {
        await upload.run('/properties', fd);
      }
      onClose();
      onSaved();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={editing ? 'Edit Property' : 'Add Property'} maxWidth="max-w-4xl">
      {error && <Alert>{error}</Alert>}
      <form onSubmit={handleSave} className="space-y-6">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Title *</label>
            <input className={inputClass} required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div>
            <label className={labelClass}>State</label>
            <select className={inputClass} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value, area: '' })}>
              <option value="">Select state</option>
              {states.map((s) => (
                <option key={s.id} value={s.name}>{s.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Area</label>
            <select className={inputClass} value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })}>
              <option value="">Select area</option>
              {areaOptions.map((a) => (
                <option key={a.id} value={a.name}>{a.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Location / Address</label>
            <input className={inputClass} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
          </div>
          <div>
            <label className={labelClass}>Property Type</label>
            <select className={inputClass} value={form.property_type} onChange={(e) => setForm({ ...form, property_type: e.target.value })}>
              {['bungalow', 'flat', 'duplex', 'terrace', 'semi-detached', 'fully-detached'].map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Listing Type</label>
            <select className={inputClass} value={form.listing_type} onChange={(e) => setForm({ ...form, listing_type: e.target.value })}>
              {['sale', 'rent', 'lease', 'management'].map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Bedrooms</label>
            <input type="number" min={0} className={inputClass} value={form.bedrooms} onChange={(e) => setForm({ ...form, bedrooms: Number(e.target.value) })} />
          </div>
          <div>
            <label className={labelClass}>Bathrooms</label>
            <input type="number" min={0} className={inputClass} value={form.bathrooms} onChange={(e) => setForm({ ...form, bathrooms: Number(e.target.value) })} />
          </div>
          <div>
            <label className={labelClass}>Price *</label>
            <input type="number" min={0} required className={inputClass} value={form.price} onChange={(e) => setForm({ ...form, price: Number(e.target.value) })} />
          </div>
          <div>
            <label className={labelClass}>Currency</label>
            <select className={inputClass} value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
              <option value="NGN">NGN</option>
              <option value="USD">USD</option>
            </select>
          </div>
          {form.listing_type === 'lease' && (
            <>
              <div>
                <label className={labelClass}>Leasing Type</label>
                <select className={inputClass} value={form.leasing_type} onChange={(e) => setForm({ ...form, leasing_type: e.target.value })}>
                  <option value="">Select</option>
                  <option value="rent">Rent</option>
                  <option value="shortlet">Shortlet</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Minimum Stay (days)</label>
                <input type="number" min={0} className={inputClass} value={form.minimum_stay} onChange={(e) => setForm({ ...form, minimum_stay: Number(e.target.value) })} />
              </div>
            </>
          )}
          <div>
            <label className={labelClass}>Featured Section</label>
            <select className={inputClass} value={form.featured_section} onChange={(e) => setForm({ ...form, featured_section: e.target.value })}>
              <option value="">Not featured</option>
              <option value="sales">Sales</option>
              <option value="leasing">Leasing</option>
              <option value="management">Management</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Featured Order</label>
            <input type="number" min={0} className={inputClass} value={form.featured_order} onChange={(e) => setForm({ ...form, featured_order: Number(e.target.value) })} />
          </div>
        </div>

        {form.listing_type === 'management' && (
          <div>
            <label className={labelClass}>Management Description</label>
            <textarea className={inputClass} rows={3} value={form.management_description} onChange={(e) => setForm({ ...form, management_description: e.target.value })} />
          </div>
        )}
        <div>
          <label className={labelClass}>Description</label>
          <textarea className={inputClass} rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>

        <div>
          <label className={labelClass}>Features</label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
            {features.map((f) => (
              <label key={f.id} className="flex items-center text-sm">
                <input
                  type="checkbox"
                  checked={form.features.includes(f.name)}
                  onChange={() => toggleFeature(f.name)}
                  className="mr-2"
                />
                {f.name}
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className={labelClass}>Images (up to 15)</label>
          <input
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            multiple
            className={inputClass}
            onChange={(e) => setImageFiles(Array.from(e.target.files || []).slice(0, 15))}
          />
          {imageFiles.length > 0 && (
            <p className="text-xs text-gray-500 mt-1">{imageFiles.length} / 15 selected</p>
          )}
          {editing && editing.images.length > 0 && (
            <p className="text-xs text-gray-500 mt-1">Existing images are kept; new files will be appended.</p>
          )}
        </div>
        <div>
          <label className={labelClass}>Videos (up to 10)</label>
          <input
            type="file"
            accept="video/*"
            multiple
            className={inputClass}
            onChange={(e) => setVideoFiles(Array.from(e.target.files || []).slice(0, 10))}
          />
          {videoFiles.length > 0 && (
            <p className="text-xs text-gray-500 mt-1">{videoFiles.length} / 10 selected</p>
          )}
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <label className="flex items-center text-sm">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="mr-2" />
            Active
          </label>
          <label className="flex items-center text-sm">
            <input type="checkbox" checked={form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })} className="mr-2" />
            Featured
          </label>
          <label className="flex items-center text-sm">
            <input type="checkbox" checked={form.is_sold} onChange={(e) => setForm({ ...form, is_sold: e.target.checked })} className="mr-2" />
            Sold
          </label>
        </div>
        {form.is_sold && (
          <div>
            <label className={labelClass}>Sold Date</label>
            <input type="date" className={inputClass} value={form.sold_date} onChange={(e) => setForm({ ...form, sold_date: e.target.value })} />
          </div>
        )}

        <UploadProgress
          percent={upload.percent}
          phase={upload.phase}
          error={upload.error}
          onCancel={upload.cancel}
        />

        <div className="flex justify-end space-x-3">
          <button type="button" onClick={onClose} className={secondaryBtn}>Cancel</button>
          <button type="submit" disabled={upload.busy} className={primaryBtn}>
            {upload.phase === 'uploading'
              ? `Uploading ${upload.percent}%`
              : upload.phase === 'processing'
                ? 'Saving...'
                : editing ? 'Save Changes' : 'Add Property'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
